import { Trans } from "@lingui/macro";
import { TooltipProvider } from "@reactive-resume/ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Outlet } from "react-router";

import { GoogleAnalytics } from "../components/analytics/google-analytics";
import { helmetContext } from "../constants/helmet";
import { useReferralCapture } from "../hooks/use-referral-capture";
import { useScrollToTop } from "../hooks/use-scroll-to-top";
import { queryClient } from "../libs/query-client";
import { AuthRefreshProvider } from "./auth-refresh";
import { CookieConsent } from "./cookie-consent";
import { DialogProvider } from "./dialog";
import { LocaleProvider } from "./locale";
import { ThemeProvider } from "./theme";
import { Toaster } from "./toaster";

const ReferralCaptureBridge = () => {
  useReferralCapture();
  return null;
};

const ScrollToTop = () => {
  useScrollToTop();
  return null;
};

/**
 * Top-level Suspense fallback for route-level code splitting (perf audit
 * item #6). Lazy auth-only routes (recharts, Tiptap, MDX, etc.) suspend
 * here while their chunk loads. The browser keeps the previous route
 * painted until React commits the new tree, so users rarely see this
 * fallback in practice.
 */
const RouteFallback = () => (
  <div
    aria-live="polite"
    aria-busy="true"
    className="flex min-h-[60vh] items-center justify-center text-muted-foreground"
  >
    <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
      <span className="size-1.5 animate-pulse rounded-full bg-primary" />
      <Trans>Loading…</Trans>
    </div>
  </div>
);

export const Providers = () => (
  <LocaleProvider>
    <HelmetProvider context={helmetContext}>
      <QueryClientProvider client={queryClient}>
        <AuthRefreshProvider>
          <GoogleAnalytics />
          <ScrollToTop />
          <ReferralCaptureBridge />
          <ThemeProvider>
            <TooltipProvider>
              <DialogProvider>
                <Suspense fallback={<RouteFallback />}>
                  <Outlet />
                </Suspense>

                <Toaster />
                <CookieConsent />
              </DialogProvider>
            </TooltipProvider>
          </ThemeProvider>
        </AuthRefreshProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </LocaleProvider>
);
